import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useAuth,
  hasLiveSession,
  landingPathForRole,
} from "../../context/AuthContext";
import { AUTH } from "../../api/endpointMap";
import { Form, Input, Button, Card } from "antd";
import { LoadingOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import BGImage from "../../assets/images/SAMPLE1.jpg";
import Logo from "../../assets/images/NabsLogoOld.png";
import NoticeAILogo from "../../assets/images/LoadingLogo.png";
import AuthFeaturesSection from "../../components/AuthFeaturesSection";
import AuthBanner from "../../assets/images/AuthBanner.jpg";
import { isPartnerSsoSession } from "../../utils/embedFlow";

const Login = () => {
  const { user, login, loading, checkReadOnlyMode, checkIsFirstPurchase } =
    useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  // Same component serves both `/login` (tenant + super_admin) and
  // `/partner-admin/login` (partner-admin only). The URL picks the BE endpoint;
  // the form / submission path is otherwise identical.
  const isPartnerLogin = location.pathname.startsWith("/partner-admin/login");
  // Defence-in-depth: a partner-SSO user should NEVER reach the login
  // page. ProtectedRoute handles the primary case (stray /-link inside
  // the iframe → inline session-expired panel). If somehow they DO land
  // here (e.g. browser back to a stale /login URL), suppress signup CTAs
  // — those would cascade them into the tenant signup flow which is
  // wrong for a session that's already been minted by a partner.
  const inEmbedSession = isPartnerSsoSession();

  const handleSubmit = (values) => {
    const { username, password } = values;
    login(username, password, isPartnerLogin ? AUTH.PARTNER_LOGIN : AUTH.LOGIN);
  };

  const clearAllCookies = () => {
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;

      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;

      const rootDomain = window.location.hostname
        .split(".")
        .slice(-2)
        .join(".");
      if (rootDomain !== window.location.hostname) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${rootDomain}`;
      }
    });
  };

  const clearAllStorage = () => {
    try {
      localStorage.clear();

      sessionStorage.clear();
    } catch (error) {
      console.error("Error clearing storage:", error);
    }
  };

  const clearAllCacheAndStorage = () => {
    clearAllCookies();
    clearAllStorage();
  };

  useEffect(() => {
    // A signed-in visitor reaching /login (bookmark, typed URL, stale tab,
    // browser back) means "take me to the app", not "sign me out". Merely
    // opening a URL must never end a session, which is exactly what the
    // unconditional clear below used to do. Send them to their own landing
    // page instead and leave storage alone.
    //
    // BOTH conditions are required, and each closes a different loop:
    //   - a live TOKEN, because the 401 handler clears the tokens but leaves
    //     `user` behind; keying off `user` alone would push an expired
    //     session into the app, earn another 401, and bounce it back here.
    //   - a `user`, because that is the very test ProtectedRoute admits on;
    //     redirecting with a token but no user would be turned straight back
    //     around at the destination.
    // Anything short of both falls through to the clear below, which is the
    // right outcome — it also disposes of whichever half was left behind.
    if (hasLiveSession() && user) {
      navigate(landingPathForRole(user?.role), { replace: true });
      return;
    }
    clearAllCacheAndStorage();
    checkReadOnlyMode();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        flexDirection: "row",
        backgroundColor: "#f0f5ff",
        position: "relative",
      }}
    >
      <div
        style={{
          flex: window.innerWidth > 768 ? 0.6 : 1,
          display: window.innerWidth > 768 ? "block" : "none",
        }}
      >
        <AuthFeaturesSection />
      </div>
      {/* <div
        style={{
          flex: 0.6,
          backgroundImage: `url(${BGImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          display: window.innerWidth > 768 ? "block" : "none",
        }}
      >
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: window.innerWidth <= 768 ? "20px" : "36px",
            fontWeight: 700,
          }}
        >
          <img src={Logo} className="w-[300px] mt-4" draggable={false} />
        </motion.h1>
      </div> */}

      <div
        style={{
          flex: window.innerWidth > 768 ? 0.5 : 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px",
          position: "relative",
          background:
            window.innerWidth <= 768
              ? `url(${BGImage}) center/cover no-repeat`
              : "#f0f5ff",
        }}
      >
        {window.innerWidth <= 768 && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 1,
            }}
          ></div>
        )}

        <div
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            zIndex: 2,
            display: inEmbedSession || isPartnerLogin ? "none" : undefined,
          }}
        >
          <Button
            type="link"
            style={{ fontWeight: "600", textDecoration: "underline" }}
            onClick={() => navigate("/signup")}
          >
            <UserOutlined /> Sign Up
          </Button>
        </div>

        <motion.img
          src={NoticeAILogo}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="noticeAIAUTHLogo"
          style={{
            marginBottom: isPartnerLogin ? 10 : 20,
            zIndex: 2,
            width: "150px",
          }}
        />

        {isPartnerLogin && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              textAlign: "center",
              marginBottom: 16,
              zIndex: 2,
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#004080",
                letterSpacing: 0.3,
              }}
            >
              Partner Admin Portal
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#5a6b7a",
                marginTop: 4,
              }}
            >
              Sign in to manage your organizations and users
            </div>
          </motion.div>
        )}

        {/* <p
          style={{
            textAlign: "center",
            color: window.innerWidth <= 768 ? "#ddd" : "#336699",
            marginBottom: 30,
            zIndex: 2,
          }}
        >
          Your one-stop solution for tax services and automation.
        </p> */}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            width: window.innerWidth <= 768 ? "90%" : "350px",
            zIndex: 2,
          }}
        >
          <Card
            style={{
              width: "100%",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Form name="login-form" onFinish={handleSubmit}>
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Please enter your email!" },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Email ID" />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  { required: true, message: "Please enter your password!" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    style={{ backgroundColor: "#004080" }}
                  >
                    {loading ? (
                      <LoadingOutlined spin size={"large"} />
                    ) : (
                      "Login"
                    )}
                  </Button>
                </motion.div>
              </Form.Item>

              <div style={{ textAlign: "center  ", marginBottom: "10px" }}>
                <span
                  style={{ color: "#004080", cursor: "pointer" }}
                  onClick={() => navigate("/reset-password")}
                >
                  Forgot Password?
                </span>
              </div>
            </Form>

            {!inEmbedSession && !isPartnerLogin && (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "15px",
                  color: "#666",
                }}
              >
                New to our platform? <br />
                <span
                  style={{
                    color: "#004080",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                  onClick={() => navigate("/signup")}
                >
                  Create an account
                </span>
              </p>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
